// Tablero de métricas (PRD Bloque K). Todo sale de dashboard_metrics (contact_notes + deals);
// aquí solo se dibuja. Con pocos datos: "sin datos todavía", nunca números de ejemplo.
import { useState } from "react";
import { addDays, mxMonday, mxYmd } from "../mxDate";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { stageLabel } from "../pipeline/useOfferStages";
import { LOSS_REASONS, money } from "../pipeline/boardData";
import { offerColor, useOffers } from "../useOffers";
import { useWeeklyGoal } from "../myday/useWeeklyGoal";
import { OUTCOMES } from "../myday/logActivity";
import { fmtDuration } from "../calls/callTimer";
import { MobilePage } from "../MobilePage";
import { useIsDueno } from "../useIsDueno";
import { Dashboard } from "../../dashboard/Dashboard";

const n = (v: number) => Math.round(v).toLocaleString("es-MX");
const pct = (a: number, b: number) => {
  if (!b) return "—";
  const p = (a / b) * 100;
  return p > 0 && p < 0.1 ? "<0.1 %" : `${Math.round(p * 10) / 10} %`;
};
const CH: Record<string, [string, string]> = {
  llamada: ["Llamadas", "var(--ch-llamada)"],
  whatsapp: ["WhatsApp", "var(--ch-whatsapp)"],
  email: ["Correos", "var(--ch-email)"],
  visita: ["Visitas", "var(--chart-4)"],
  reunion: ["Reuniones", "var(--chart-5)"],
};
const SMALL = 30; // debajo de esto, las tasas se marcan "muestra chica"

const PERIODS = [
  ["semana", "Esta semana"],
  ["8sem", "8 semanas"],
  ["mes", "Este mes"],
  ["todo", "Todo"],
] as const;
type Period = (typeof PERIODS)[number][0];
const range = (p: Period): [string, string] => {
  const today = mxYmd(); // fechas de México, sin importar el reloj del aparato
  const monday = mxMonday();
  if (p === "semana") return [monday, today];
  if (p === "8sem") return [addDays(monday, -49), today];
  if (p === "mes") return [today.slice(0, 8) + "01", today];
  return ["2024-01-01", today];
};

type Metrics = {
  supuestos: Record<string, number>;
  toques: number;
  demos: number;
  semanas: { semana: string; canal: string; n: number }[];
  canales: {
    canal: string;
    toques: number;
    respuestas: number;
    demos: number;
  }[];
  llamadas: { total: number; decisor: number };
  horas: { dow: number; hora: number; llamadas: number; decisor: number }[];
  demos_cadencia: { cadencia: string; demos: number }[];
  embudo: {
    slug: string;
    etapa: string;
    pos: number;
    llegaron: number;
    perdidas: number;
  }[];
  ganadas: { slug: string; n: number; valor: number }[];
  ganadas_mes: { n: number; valor: number };
  perdidas: { motivo: string | null; n: number }[];
  pipeline: {
    slug: string;
    nombre: string;
    color: string | null;
    abiertas: number;
    demo_o_mas: number;
    valor: number;
    pronostico: number;
    termometro: number | null;
    ganadas: number;
  }[];
  // Bloque L: duración de llamada
  duracion_resultado: { resultado: string; n: number; seg_prom: number }[];
  duracion_producto: { slug: string; n: number; seg_prom: number }[];
  duracion_horario: {
    dow: number;
    hora: number;
    n: number;
    seg_prom: number;
  }[];
  minutos_semana: { semana: string; minutos: number }[];
  pct_conversacion_real: number | null;
};

// Bloque T.4: qué variante de mensaje vende (vista variant_stats, sin filtro de periodo)
type VariantStat = {
  template_id: number;
  offer_slug: string;
  channel: string;
  step_key: string | null;
  variant: string;
  origin: string;
  envios: number;
  respuestas: number;
  demos: number;
  compromisos: number;
  muestra_chica: boolean;
};

const Panel = ({
  title,
  cap,
  children,
  className,
}: {
  title: string;
  cap?: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <Card className={cn("min-w-0 gap-2 p-4", className)}>
    <h2 className="text-base font-extrabold">{title}</h2>
    {cap && <p className="text-xs text-muted-foreground">{cap}</p>}
    {children}
  </Card>
);
const Empty = () => (
  <p className="text-sm text-muted-foreground">Sin datos todavía.</p>
);
const Kpi = ({
  cap,
  v,
  sub,
}: {
  cap: string;
  v: React.ReactNode;
  sub?: React.ReactNode;
}) => (
  <Card className="gap-0.5 p-4">
    <span className="text-xs text-muted-foreground">{cap}</span>
    <span className="text-2xl font-extrabold tabular-nums">{v}</span>
    {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
  </Card>
);
const Bar = ({
  label,
  value,
  max,
  color,
  right,
}: {
  label: string;
  value: number;
  max: number;
  color?: string;
  right?: string;
}) => (
  <div
    className="grid grid-cols-[minmax(6rem,9rem)_1fr_auto] items-center gap-2 text-sm"
    title={`${label}: ${n(value)}`}
  >
    <span className="truncate text-muted-foreground">{label}</span>
    <span className="h-3 rounded-full bg-secondary">
      <i
        className="block h-3 rounded-full"
        style={{
          width: `${max ? (value / max) * 100 : 0}%`,
          background: color ?? "var(--primary)",
        }}
      />
    </span>
    <b className="tabular-nums">{right ?? n(value)}</b>
  </div>
);

/** Tablero (PRD K): filtros por periodo y oferta. */
export const Tablero = () => {
  const { data: offers } = useOffers();
  const isDueno = useIsDueno();
  const [period, setPeriod] = useState<Period>("8sem");
  const [offer, setOffer] = useState<string>("");
  const [from, to] = range(period);
  const { data: goal } = useWeeklyGoal();
  const { data: M, isPending } = useQuery({
    queryKey: ["dashboard_metrics", from, to, offer],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "dashboard_metrics",
        {
          p_from: from,
          p_to: to,
          p_offer: offer || null,
        },
      );
      if (error) throw error;
      return data as Metrics;
    },
  });
  const [funnelSlug, setFunnelSlug] = useState<string>("");
  const { data: variantStats } = useQuery({
    queryKey: ["variant_stats"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("variant_stats")
        .select(
          "template_id, offer_slug, channel, step_key, variant, origin, envios, respuestas, demos, compromisos, muestra_chica",
        )
        .eq("archived", false);
      if (error) throw error;
      return (data ?? []) as VariantStat[];
    },
  });

  if (!offers || isPending || !M) return <Skeleton className="h-40 w-full" />;
  const A = M.supuestos;
  const name = (slug: string) =>
    offers.find((o) => o.slug === slug)?.name ?? slug;

  // toques por semana y canal (barras apiladas)
  const weeks = [...new Set(M.semanas.map((s) => s.semana))].sort();
  const chans = [...new Set(M.semanas.map((s) => s.canal))];
  const wk = (w: string, c: string) =>
    M.semanas.find((s) => s.semana === w && s.canal === c)?.n ?? 0;
  const wmax = Math.max(
    1,
    ...weeks.map((w) => chans.reduce((a, c) => a + wk(w, c), 0)),
  );

  const fSlug = offer || funnelSlug || M.embudo[0]?.slug || offers[0]?.slug;
  const funnel = M.embudo.filter((e) => e.slug === fSlug);
  const fmax = Math.max(1, ...funnel.map((f) => f.llegaron));

  const hours = [...Array(13)].map((_, i) => i + 8); // 8:00 a 20:00
  const days = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
  const hcell = (d: number, h: number) =>
    M.horas.find((x) => x.dow === d && x.hora === h);
  const lmax = Math.max(1, ...M.perdidas.map((p) => p.n));
  const lossName = (k: string | null) =>
    LOSS_REASONS.find(([v]) => v === k)?.[1] ?? "Sin motivo";
  const won = M.ganadas.reduce((a, g) => a + g.n, 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div
          className="inline-flex overflow-hidden rounded-lg border"
          role="group"
          aria-label="Periodo"
        >
          {PERIODS.map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setPeriod(k)}
              className={cn(
                "whitespace-nowrap px-3 py-1.5 text-sm max-sm:px-2 max-sm:text-xs",
                period === k
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-secondary",
              )}
            >
              {l}
            </button>
          ))}
        </div>
        <select
          className="rounded-lg border bg-background px-2 py-1.5 text-sm"
          value={offer}
          onChange={(e) => setOffer(e.target.value)}
          aria-label="Oferta"
        >
          <option value="">Todas las ofertas</option>
          {offers.map((o) => (
            <option key={o.slug} value={o.slug}>
              {o.name}
            </option>
          ))}
        </select>
        <span className="text-xs text-muted-foreground">
          {from.split("-").reverse().join("/")} a{" "}
          {to.split("-").reverse().join("/")}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          cap="Demos esta semana"
          v={
            <>
              {goal?.thisWeek ?? 0}{" "}
              <span className="text-base text-muted-foreground">
                / meta {goal?.goal ?? 5}
              </span>
            </>
          }
          sub={`Racha: ${goal?.streak ?? 0} semana${goal?.streak === 1 ? "" : "s"} cumpliendo`}
        />
        <Kpi
          cap="Toques en el periodo"
          v={n(M.toques)}
          sub={
            M.toques
              ? `${M.demos} demo${M.demos === 1 ? "" : "s"} agendada${M.demos === 1 ? "" : "s"}`
              : "sin datos todavía"
          }
        />
        <Kpi
          cap="Hablé con el decisor"
          v={M.llamadas.total ? pct(M.llamadas.decisor, M.llamadas.total) : "—"}
          sub={
            M.llamadas.total
              ? `${M.llamadas.decisor} de ${M.llamadas.total} llamadas · supuesto ${A.decisor ?? 12} %${M.llamadas.total < SMALL ? " · muestra chica" : ""}`
              : "sin llamadas todavía"
          }
        />
        <Kpi
          cap="Ganados este mes"
          v={n(M.ganadas_mes.n)}
          sub={M.ganadas_mes.n ? money(M.ganadas_mes.valor) : "ninguno todavía"}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[7fr_5fr]">
        <Panel title="Toques por semana y canal">
          {!weeks.length ? (
            <Empty />
          ) : (
            <>
              <div className="flex flex-wrap gap-3 text-xs">
                {chans.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1">
                    <i
                      className="size-2.5 rounded-sm"
                      style={{ background: CH[c]?.[1] }}
                    />
                    {CH[c]?.[0] ?? c}
                  </span>
                ))}
              </div>
              <div className="flex h-44 items-end gap-2 overflow-x-auto pt-2">
                {weeks.map((w) => {
                  const tot = chans.reduce((a, c) => a + wk(w, c), 0);
                  return (
                    <div
                      key={w}
                      className="flex min-w-10 flex-1 flex-col items-center gap-1"
                    >
                      <span className="text-[11px] font-bold tabular-nums">
                        {tot}
                      </span>
                      <div
                        className="flex w-7 flex-col-reverse overflow-hidden rounded-t-md"
                        style={{ height: `${(tot / wmax) * 120}px` }}
                        title={`Semana del ${w.slice(8)}/${w.slice(5, 7)}: ${chans.map((c) => `${CH[c]?.[0] ?? c} ${wk(w, c)}`).join(" · ")}`}
                      >
                        {chans.map((c) => (
                          <i
                            key={c}
                            style={{
                              height: `${(wk(w, c) / (tot || 1)) * 100}%`,
                              background: CH[c]?.[1],
                            }}
                          />
                        ))}
                      </div>
                      <span className="text-[11px] text-muted-foreground">
                        {w.slice(8)}/{w.slice(5, 7)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Panel>

        <Panel
          title="Embudo por oferta"
          cap="Oportunidades creadas en el periodo que llegaron a cada etapa"
        >
          {!offer && (
            <select
              className="self-start rounded-md border bg-background px-2 py-1 text-sm"
              value={fSlug}
              onChange={(e) => setFunnelSlug(e.target.value)}
              aria-label="Oferta del embudo"
            >
              {offers.map((o) => (
                <option key={o.slug} value={o.slug}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
          {!funnel.length || !funnel[0].llegaron ? (
            <Empty />
          ) : (
            <div className="flex flex-col gap-1.5">
              {funnel.map((f) => (
                <Bar
                  key={f.etapa}
                  label={stageLabel(f.etapa)}
                  value={f.llegaron}
                  max={fmax}
                  color={offerColor(offers.find((o) => o.slug === fSlug)!)}
                />
              ))}
              {!!funnel[0].perdidas && (
                <span className="text-xs text-muted-foreground">
                  {funnel[0].perdidas} perdida
                  {funnel[0].perdidas === 1 ? "" : "s"} (cuentan en la etapa a
                  la que llegaron)
                </span>
              )}
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="Tasas reales vs. supuestos"
          cap="Respuesta = habló con el decisor, respondió o agendó demo."
        >
          {!M.canales.length ? (
            <Empty />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="py-1 pr-2">Canal</th>
                    <th className="pr-2 text-right">Toques</th>
                    <th className="pr-2 text-right">Respuesta real</th>
                    <th className="pr-2 text-right">Supuesto</th>
                    <th className="text-right">A demo</th>
                  </tr>
                </thead>
                <tbody>
                  {M.canales.map((c) => (
                    <tr key={c.canal} className="border-t">
                      <td className="py-1 pr-2">
                        {CH[c.canal]?.[0] ?? c.canal}
                        {c.toques < SMALL && (
                          <span className="ml-1 text-[11px] text-muted-foreground">
                            muestra chica
                          </span>
                        )}
                      </td>
                      <td className="pr-2 text-right tabular-nums">
                        {n(c.toques)}
                      </td>
                      <td className="pr-2 text-right tabular-nums">
                        {pct(c.respuestas, c.toques)}
                      </td>
                      <td className="pr-2 text-right tabular-nums">
                        {A[c.canal] != null ? `${A[c.canal]} %` : "—"}
                      </td>
                      <td className="text-right tabular-nums">
                        {pct(c.demos, c.toques)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel
          title="Cuándo contestan los decisores"
          cap="% de llamadas que llegaron al decisor, por día y hora (hora de México)"
        >
          {!M.horas.length ? (
            <Empty />
          ) : (
            <div className="overflow-x-auto">
              <div
                className="grid min-w-[520px] gap-0.5 text-[10.5px]"
                style={{
                  gridTemplateColumns: `2.5rem repeat(${hours.length}, 1fr)`,
                }}
              >
                <span />
                {hours.map((h) => (
                  <span key={h} className="text-center text-muted-foreground">
                    {h}h
                  </span>
                ))}
                {days.map((d, i) => (
                  <div key={d} className="contents">
                    <span className="text-muted-foreground">{d}</span>
                    {hours.map((h) => {
                      const c = hcell(i + 1, h);
                      const r = c ? c.decisor / c.llamadas : 0;
                      return (
                        <span
                          key={h}
                          className="grid h-6 place-items-center rounded-sm"
                          title={
                            c
                              ? `${d} ${h}:00 · ${c.decisor} de ${c.llamadas} llamadas llegaron al decisor`
                              : `${d} ${h}:00 · sin llamadas`
                          }
                          style={{
                            background: c
                              ? `color-mix(in oklab, var(--good) ${Math.round(10 + r * 80)}%, transparent)`
                              : "var(--secondary)",
                          }}
                        >
                          {c ? c.llamadas : ""}
                        </span>
                      );
                    })}
                  </div>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                El número es cuántas llamadas hubo; más verde = más llegaron al
                decisor.
              </p>
            </div>
          )}
        </Panel>

        <Panel
          title="Duración de llamada"
          cap="Promedio por resultado y producto, minutos hablados por semana, y % de llamadas de más de 2 min (“conversación real”)."
        >
          {!M.duracion_resultado.length && !M.duracion_producto.length ? (
            <Empty />
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-sm">
                {M.pct_conversacion_real != null ? (
                  <>
                    <b className="text-lg">{M.pct_conversacion_real}%</b> de las
                    llamadas duraron más de 2 minutos.
                  </>
                ) : (
                  "Sin datos todavía"
                )}
              </p>
              {M.duracion_resultado.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Por resultado
                  </p>
                  {M.duracion_resultado.map((r) => (
                    <Bar
                      key={r.resultado}
                      label={
                        OUTCOMES.find((o) => o.value === r.resultado)?.label ??
                        r.resultado
                      }
                      value={r.seg_prom}
                      max={Math.max(
                        1,
                        ...M.duracion_resultado.map((x) => x.seg_prom),
                      )}
                      right={fmtDuration(r.seg_prom)}
                    />
                  ))}
                </div>
              )}
              {M.duracion_producto.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Por producto
                  </p>
                  {M.duracion_producto.map((r) => (
                    <Bar
                      key={r.slug}
                      label={name(r.slug)}
                      value={r.seg_prom}
                      max={Math.max(
                        1,
                        ...M.duracion_producto.map((x) => x.seg_prom),
                      )}
                      color={offerColor(offers.find((o) => o.slug === r.slug))}
                      right={fmtDuration(r.seg_prom)}
                    />
                  ))}
                </div>
              )}
              {M.minutos_semana.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Minutos hablados por semana
                  </p>
                  {M.minutos_semana.map((s) => (
                    <Bar
                      key={s.semana}
                      label={new Date(s.semana).toLocaleDateString("es-MX", {
                        day: "2-digit",
                        month: "short",
                      })}
                      value={s.minutos}
                      max={Math.max(
                        1,
                        ...M.minutos_semana.map((x) => x.minutos),
                      )}
                      right={`${s.minutos} min`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </Panel>

        <Panel
          title="Motivos de pérdida"
          cap="Si domina “Precio”, revisa la propuesta; si domina “No le duele”, revisa a quién le llamas."
        >
          {!M.perdidas.length ? (
            <Empty />
          ) : (
            <div className="flex flex-col gap-1.5">
              {M.perdidas.map((p) => (
                <Bar
                  key={p.motivo ?? "x"}
                  label={lossName(p.motivo)}
                  value={p.n}
                  max={lmax}
                  color="var(--critical)"
                />
              ))}
            </div>
          )}
        </Panel>

        <Panel
          title="Demos por cadencia"
          cap="Demos agendadas en el periodo según la cadencia con que se trabajó la oportunidad."
        >
          {!M.demos_cadencia.length ? (
            <Empty />
          ) : (
            <div className="flex flex-col gap-1.5">
              {M.demos_cadencia.map((c) => (
                <Bar
                  key={c.cadencia}
                  label={c.cadencia}
                  value={c.demos}
                  max={Math.max(...M.demos_cadencia.map((x) => x.demos))}
                />
              ))}
            </div>
          )}
          {!!won && (
            <p className="text-xs text-muted-foreground">
              Ganadas en el periodo:{" "}
              {M.ganadas
                .map((g) => `${name(g.slug)} ${g.n} (${money(g.valor)})`)
                .join(" · ")}
            </p>
          )}
        </Panel>
      </div>

      <Panel
        title="Qué mensajes venden"
        cap="Variantes A/B con al menos un envío, ordenadas por % de respuesta (respondió, habló con decisor o demo agendada)."
      >
        {(() => {
          const withSample = (variantStats ?? []).filter((v) => v.envios > 0);
          if (!withSample.length) return <Empty />;
          const top = [...withSample]
            .sort(
              (a, b) =>
                b.respuestas / b.envios - a.respuestas / a.envios ||
                b.envios - a.envios,
            )
            .slice(0, 12);
          return (
            <div className="flex flex-col gap-1.5">
              {top.map((v) => {
                const rate = Math.round((v.respuestas / v.envios) * 100);
                return (
                  <Bar
                    key={v.template_id}
                    label={`${name(v.offer_slug)} · ${v.step_key ?? v.channel} · ${v.variant}`}
                    value={rate}
                    max={100}
                    right={`${rate}% (${v.respuestas}/${v.envios})${v.muestra_chica ? " · muestra chica" : ""}`}
                  />
                );
              })}
            </div>
          );
        })()}
      </Panel>

      <Panel
        title="Pipeline por oferta"
        cap="Hoy. Pronóstico = Σ valor × probabilidad (la probabilidad usa el termómetro de cada oportunidad)."
      >
        {!M.pipeline.some((p) => p.abiertas || p.ganadas) ? (
          <Empty />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-1 pr-2">Oferta</th>
                  <th className="pr-2 text-right">Abiertas</th>
                  <th className="pr-2 text-right">En demo o más</th>
                  <th className="pr-2 text-right">Valor en juego</th>
                  <th className="pr-2 text-right">Pronóstico</th>
                  <th className="pr-2 text-right">Termómetro prom.</th>
                  <th className="text-right">Ganadas</th>
                </tr>
              </thead>
              <tbody>
                {M.pipeline.map((p) => (
                  <tr key={p.slug} className="border-t">
                    <td className="py-1 pr-2">
                      <Link
                        to={`/board/${offers.find((o) => o.slug === p.slug)?.id}`}
                        className="inline-flex items-center gap-1.5 hover:underline"
                      >
                        <i
                          className="size-2.5 rounded-full"
                          style={{
                            background: offerColor(
                              offers.find((o) => o.slug === p.slug) ?? {
                                slug: p.slug,
                                color: p.color,
                              },
                            ),
                          }}
                        />
                        {p.nombre}
                      </Link>
                    </td>
                    <td className="pr-2 text-right tabular-nums">
                      {n(p.abiertas)}
                    </td>
                    <td className="pr-2 text-right tabular-nums">
                      {n(p.demo_o_mas)}
                    </td>
                    <td className="pr-2 text-right tabular-nums">
                      {money(p.valor) || "—"}
                    </td>
                    <td className="pr-2 text-right tabular-nums">
                      {money(p.pronostico) || "—"}
                    </td>
                    <td className="pr-2 text-right tabular-nums">
                      {p.termometro ?? "—"}
                    </td>
                    <td className="text-right tabular-nums">{n(p.ganadas)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* directory_coverage scans the whole directory: owner only */}
      {isDueno && <Coverage />}
    </div>
  );
};

/** Cobertura del directorio (se conserva por decisión de Ian). */
const Coverage = () => {
  const { data: offers } = useOffers();
  const { data, isPending } = useQuery({
    queryKey: ["directory_coverage"],
    staleTime: 10 * 60 * 1000, // recorre el directorio completo (~1.5 s)
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("directory_coverage")
        .select("*");
      if (error) throw error;
      return data as {
        slug: string;
        empresas: number;
        tier_a: number;
        en_crm: number;
      }[];
    },
  });
  return (
    <Panel title="Cobertura del directorio">
      {isPending || !offers ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr>
                <th className="py-1 pr-2">Oferta</th>
                <th className="pr-2 text-right">Empresas</th>
                <th className="pr-2 text-right">Nivel A</th>
                <th className="pr-2 text-right">En CRM</th>
                <th className="text-right">% en CRM</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => {
                const rows = data?.filter((c) => c.slug === o.slug) ?? [];
                const sum = (k: "empresas" | "tier_a" | "en_crm") =>
                  rows.reduce((s, r) => s + Number(r[k]), 0);
                return (
                  <tr key={o.slug} className="border-t">
                    <td className="py-1 pr-2">{o.name}</td>
                    <td className="pr-2 text-right tabular-nums">
                      {n(sum("empresas"))}
                    </td>
                    <td className="pr-2 text-right tabular-nums">
                      {n(sum("tier_a"))}
                    </td>
                    <td className="pr-2 text-right tabular-nums">
                      {n(sum("en_crm"))}
                    </td>
                    <td className="text-right tabular-nums">
                      {pct(sum("en_crm"), sum("empresas"))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

/** Página en celular (/tablero). */
export const TableroPage = () => (
  <MobilePage title="Tablero">
    <Tablero />
  </MobilePage>
);

/** Tablero en computadora; debajo, la actividad reciente de Atomic. */
export const DesktopDashboard = () => (
  <div className="mt-4 flex flex-col gap-10">
    <div>
      <h1 className="mb-3 text-2xl font-extrabold tracking-tight">Tablero</h1>
      <Tablero />
    </div>
    <section>
      <h2 className="mb-3 text-xl font-semibold">Actividad reciente</h2>
      <Dashboard />
    </section>
  </div>
);
